/**
 * The one representation of "change this text" in the whole application.
 *
 * Repair proposals, quick fixes and (later) inline tree editing all produce `TextEdit[]`
 * rather than a rewritten document. Three properties follow from that and each is load
 * bearing:
 *
 *  - a fix survives `structuredClone` through the Worker, because it is data and not a
 *    closure (ADR-0008);
 *  - the whole set applies as ONE CodeMirror transaction, so Mod-Z undoes a repair in one
 *    press (ADR-0006);
 *  - overlapping edits are REJECTED rather than merged by guesswork, which is exactly the
 *    failure mode that made the legacy fixer corrupt documents (ADR-0012, step 4).
 */

/** Replace `[from, to)` with `insert`. `from === to` is a pure insertion. */
export interface TextEdit {
  readonly from: number;
  readonly to: number;
  readonly insert: string;
}

/**
 * Thrown by {@link applyTextEdits} when the edit set cannot be applied unambiguously.
 * Carries the offending set so a test or a log line can show what was asked for.
 */
export class OverlappingEditsError extends Error {
  readonly edits: readonly TextEdit[];

  constructor(edits: readonly TextEdit[]) {
    super(`applyTextEdits: refusing to apply ${edits.length} edits that overlap or are invalid`);
    this.name = 'OverlappingEditsError';
    this.edits = edits;
  }
}

/** `from` and `to` are non-negative integers in order. */
function isWellFormed(edit: TextEdit): boolean {
  return (
    Number.isInteger(edit.from) &&
    Number.isInteger(edit.to) &&
    edit.from >= 0 &&
    edit.to >= edit.from
  );
}

/**
 * Sort and validate; return `null` instead of throwing when the edits conflict.
 *
 * Ordering is by `from`, then by `to`, and is STABLE: two pure insertions at the same
 * offset keep their input order, so the result is deterministic rather than arbitrary.
 * `Array.prototype.sort` is specified as stable since ES2019.
 *
 * Two edits conflict when the second one starts strictly before the first one ends.
 * Touching edits (`a.to === b.from`) are fine, and so are several insertions at one offset
 * — neither can disagree about a single code unit.
 */
export function normalizeEdits(edits: readonly TextEdit[]): TextEdit[] | null {
  for (const edit of edits) {
    if (!isWellFormed(edit)) return null;
  }

  const sorted = [...edits].sort((a, b) => a.from - b.from || a.to - b.to);

  for (let i = 1; i < sorted.length; i += 1) {
    const previous = sorted[i - 1];
    const current = sorted[i];
    if (!previous || !current) return null;
    if (current.from < previous.to) return null;
  }

  return sorted;
}

/**
 * Apply `edits` to `source` in a single left-to-right pass.
 *
 * @throws OverlappingEditsError when {@link normalizeEdits} rejects the set. The repair
 * driver already discards conflicting proposals before it gets here, so this throw doubles
 * as the assertion that the driver's conflict pass works (ADR-0012, step 5).
 * @throws RangeError when an edit points outside `source`.
 */
export function applyTextEdits(source: string, edits: readonly TextEdit[]): string {
  if (edits.length === 0) return source;

  const ordered = normalizeEdits(edits);
  if (ordered === null) throw new OverlappingEditsError(edits);

  const last = ordered[ordered.length - 1];
  if (last && last.to > source.length) {
    throw new RangeError(
      `applyTextEdits: edit [${last.from}, ${last.to}) is outside a source of length ${source.length}`,
    );
  }

  const parts: string[] = [];
  let cursor = 0;

  for (const edit of ordered) {
    if (edit.from > cursor) parts.push(source.slice(cursor, edit.from));
    if (edit.insert.length > 0) parts.push(edit.insert);
    cursor = edit.to;
  }

  if (cursor < source.length) parts.push(source.slice(cursor));
  return parts.join('');
}
